import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import {
  Wallet, Activity, ShieldOff, AlertTriangle, FileCode2,
  Lock, Sparkles, Zap, ArrowRight, Check, Crown,
} from "lucide-react";
import Header from "../components/Header";
import Footer from "../components/Footer";
import ConsultCtaBand from "../components/ConsultCtaBand";
import SeverityBadge from "../components/SeverityBadge";
import api from "../lib/api";
import AtlasCsvImport from "../components/AtlasCsvImport";
import { useUserAuth } from "../lib/userAuth";

import useDocumentTitle from "../hooks/useDocumentTitle";
import useQelliWarmPulse from "../hooks/useQelliWarmPulse";
import usePageMeta from "../hooks/usePageMeta";
import M from "../lib/pageMetaConfig";

const API = (process.env.REACT_APP_BACKEND_URL || "").replace(/\/$/, "");

// One unified Atlas page with three personas:
//   1. Anonymous      → marketing hero + frozen demo workspace + signup CTA.
//   2. Free, has quota → real workspace + "1 free import remaining" pill.
//   3. Free, used up   → real workspace LOCKED-DOWN + paywall card.
//   4. Paid (pro/team) → real workspace + monthly usage pill.
export default function AtlasPage() {
  usePageMeta(M.atlas);
  const { account, loading: authLoading } = useUserAuth();
  const [data,   setData]   = useState(null);
  const [usage,  setUsage]  = useState(null);
  const [demo,   setDemo]   = useState(null);
  const [loading, setLoading] = useState(true);
  const [needsSignin, setNeedsSignin] = useState(false);

  useEffect(() => {
    if (authLoading) return;
    setLoading(true);
    if (!account) {
      // Anonymous → fetch the public demo for the Atlas marketing page.
      fetch(`${API}/api/atlas/preview`)
        .then((r) => r.json())
        .then((d) => { setDemo(d); setLoading(false); })
        .catch(() => setLoading(false));
      return;
    }
    // Signed-in → fetch the real workspace + usage in parallel.
    Promise.all([
      api.workspace().catch((e) => {
        // 401 with requires_signin → a stale/expired cookie. Force the
        // anon-flow render so the user can re-sign-in.
        if (e?.response?.status === 401) {
          setNeedsSignin(true);
          return null;
        }
        throw e;
      }),
      fetch(`${API}/api/atlas/usage`, { credentials: "include" })
        .then((r) => (r.ok ? r.json() : null)),
    ]).then(([ws, u]) => {
      if (ws) setData(ws);
      if (u)  setUsage(u);
      setLoading(false);
    }).catch(() => setLoading(false));
  }, [account, authLoading]);

  // Iter198 — Qelli warm-pulse. Prospects landing on /atlas via a
  // LinkedIn / X / email share are high-intent treasury buyers; have
  // Qelli wave at them with a treasury-monitoring intent.
  useQelliWarmPulse({
    enabled:    true,
    source:     "atlas-landing",
    intentText: "Want me to explain what these wallet scores mean?",
    prompt:
      "Walk me through how Atlas scores my wallets — what each tier " +
      "(Critical / High / Medium / Low / Quasi-resistant) means, how " +
      "the 0–100 score is computed, and what I should do about it.",
  });

  // --- Render branches ---

  if (authLoading || loading) {
    return (
      <Frame>
        <div className="qt-card mt-8 animate-pulse" style={{ minHeight: 320 }} />
      </Frame>
    );
  }

  if (!account || needsSignin) {
    return (
      <Frame>
        <AnonymousLanding demo={demo} />
      </Frame>
    );
  }

  // Signed-in flows below.
  const isPaid = usage && (usage.plan === "pro" || usage.plan === "team" || usage.kind === "unlimited");
  const quotaExceeded = usage && !usage.can_use;

  return (
    <Frame>
      <DashHero account={account} usage={usage} />

      {/* Plan-aware paywall / usage card */}
      <UsageCard usage={usage} />

      {/* CSV import — keep visible (it returns 402 + we render a paywall card below) */}
      <div data-paywalled={quotaExceeded ? "true" : "false"}>
        {quotaExceeded
          ? <PaywallCard usage={usage} />
          : <AtlasCsvImport
              usage={usage}
              onUsageRefresh={(u) => setUsage(u)}
            />}
      </div>

      {data && <PortfolioBlock data={data} />}
      {!isPaid && <UpgradeStrip usage={usage} />}
    </Frame>
  );
}

// ---------------------------------------------------------------------------
// Layout shell
// ---------------------------------------------------------------------------
function Frame({ children }) {
  return (
    <div className="min-h-screen qt-radial-bg">
      <Header />
      <div className="max-w-[1400px] mx-auto px-6 sm:px-8 py-10">
        {children}
      </div>
      <ConsultCtaBand source="atlas-bottom-cta" variant="card" maxWidth={1400} />
      <Footer />
    </div>
  );
}

// ---------------------------------------------------------------------------
// 1. Anonymous landing — marketing hero + frozen demo + signup CTA
// ---------------------------------------------------------------------------
function AnonymousLanding({ demo }) {
  return (
    <>
      <section className="grid grid-cols-1 lg:grid-cols-12 gap-8 items-center mb-10">
        <div className="lg:col-span-7">
          <div className="qt-label" style={{ color: "var(--qt-cyan)" }}>QORTRACE ATLAS</div>
          <h1 className="qt-h2 text-4xl sm:text-5xl mt-2" data-testid="atlas-anon-title">
            Multi-chain portfolio<br/>exposure intelligence.
          </h1>
          <p className="mt-4 max-w-xl text-base" style={{ color: "var(--qt-text-2)", lineHeight: 1.7 }}>
            Drop in a CSV of wallets and contracts across 18 chains. Atlas batch-scans the lot,
            flags every key that's already exposed to a future quantum adversary, totals up
            balance-at-risk in USD, and gives you a single pane of glass for portfolio-wide
            cryptographic posture.
          </p>
          <div className="mt-6 flex flex-wrap gap-3">
            <Link
              to="/signin?next=/atlas"
              data-testid="atlas-anon-signup-cta"
              className="qt-btn qt-btn-primary"
            >
              Start free — 1 portfolio import <ArrowRight size={14} />
            </Link>
            <Link to="/pricing" data-testid="atlas-anon-pricing-cta" className="qt-btn qt-btn-secondary">
              See plans
            </Link>
          </div>
          <ul className="mt-6 grid grid-cols-1 sm:grid-cols-2 gap-y-2 gap-x-6 text-sm" style={{ color: "var(--qt-text-2)" }}>
            <li className="flex items-center gap-2"><Check size={14} style={{ color: "#1FDD83" }} />Up to 100 addresses per CSV</li>
            <li className="flex items-center gap-2"><Check size={14} style={{ color: "#1FDD83" }} />18 chains: BTC, ETH, SOL, ARB, BASE…</li>
            <li className="flex items-center gap-2"><Check size={14} style={{ color: "#1FDD83" }} />Severity heatmap + balance-at-risk</li>
            <li className="flex items-center gap-2"><Check size={14} style={{ color: "#1FDD83" }} />Per-row report links</li>
          </ul>
        </div>
        <div className="lg:col-span-5">
          <PreviewCard />
        </div>
      </section>

      <div className="qt-card relative" style={{ overflow: "hidden" }}>
        <div className="qt-label" style={{ color: "var(--qt-text-3)" }}>SAMPLE PORTFOLIO · DEMO DATA</div>
        <div className="opacity-90">
          {demo ? <PortfolioBlock data={demo} demo /> : (
            <div className="qt-card mt-4 animate-pulse" style={{ minHeight: 200 }} />
          )}
        </div>
        {/* Soft fade overlay near the bottom + signup nudge */}
        <div className="absolute inset-x-0 bottom-0 pointer-events-none"
             style={{ height: 140, background: "linear-gradient(to top, rgba(4,9,20,0.95), transparent)" }} />
        <div className="absolute inset-x-0 bottom-4 flex justify-center">
          <Link
            to="/signin?next=/atlas"
            data-testid="atlas-anon-bottom-cta"
            className="qt-btn qt-btn-primary"
            style={{ pointerEvents: "auto" }}
          >
            Sign up free to import your real portfolio <ArrowRight size={14} />
          </Link>
        </div>
      </div>
    </>
  );
}

function PreviewCard() {
  return (
    <div
      className="qt-card relative overflow-hidden"
      style={{ borderColor: "rgba(0,229,255,0.4)" }}
    >
      <div className="absolute inset-0 qt-hero-grid pointer-events-none opacity-30" />
      <div className="qt-label" style={{ color: "var(--qt-cyan)" }}>SAMPLE METRICS</div>
      <div className="grid grid-cols-2 gap-3 mt-4">
        <Metric icon={<Wallet size={16}/>}      label="ADDRESSES"    value="4" />
        <Metric icon={<ShieldOff size={16}/>}   label="EXPOSED"      value="2" accent="#FF8000" />
        <Metric icon={<AlertTriangle size={16}/>} label="BAL AT RISK"
                value="$2.05M" accent="#FF2A2A" />
        <Metric icon={<FileCode2 size={16}/>}   label="CONTRACTS"    value="2" />
      </div>
      <div className="mt-4 text-xs qt-mono opacity-80" style={{ letterSpacing: "0.08em", color: "var(--qt-text-3)" }}>
        DEMO · YOUR REAL PORTFOLIO LOOKS LIKE THIS AFTER ONE CSV IMPORT
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// 2. Signed-in dashboard hero + usage card
// ---------------------------------------------------------------------------
function DashHero({ account, usage }) {
  return (
    <div className="flex items-end justify-between flex-wrap gap-3" data-testid="atlas-dash-hero">
      <div>
        <div className="qt-label">QORTRACE ATLAS</div>
        <h1 className="qt-h2 text-4xl sm:text-5xl mt-2">Portfolio Exposure Dashboard</h1>
        <p className="mt-3 max-w-2xl" style={{ color: "var(--qt-text-2)" }}>
          Aggregate balance-at-risk, exposed-address counts, and contract finding heatmap
          across every scan and analysis in your workspace.
        </p>
      </div>
      <div className="flex gap-2 items-center">
        {usage && <PlanPill usage={usage} />}
        <Link to="/scan" data-testid="atlas-cta-scan" className="qt-btn qt-btn-primary">+ New scan</Link>
        <Link to="/contract" data-testid="atlas-cta-contract" className="qt-btn qt-btn-secondary">+ Analyze contract</Link>
      </div>
    </div>
  );
}

function PlanPill({ usage }) {
  const plan = (usage.plan || "free").toUpperCase();
  const accent = plan === "FREE" ? "#9CAEC9" : plan === "PRO" ? "#00E5FF" : "#FFB800";
  const label = usage.kind === "unlimited"
    ? `${plan} · UNLIMITED`
    : usage.kind === "monthly"
      ? `${plan} · ${usage.used}/${usage.limit} THIS MONTH`
      : `${plan} · ${usage.used}/${usage.limit} LIFETIME`;
  return (
    <span
      data-testid="atlas-plan-pill"
      className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full text-[11px] qt-mono"
      style={{
        border: `1px solid ${accent}`,
        background: `${accent}1A`,
        color: accent,
        letterSpacing: "0.10em",
      }}
    >
      {plan === "FREE" ? <Sparkles size={11}/> : <Crown size={11}/>}
      {label}
    </span>
  );
}

function UsageCard({ usage }) {
  if (!usage) return null;
  if (usage.kind === "unlimited") return null;
  if (!usage.can_use) return null;            // PaywallCard handles that case.
  return (
    <div
      className="qt-card mt-6 flex items-center justify-between flex-wrap gap-3"
      data-testid="atlas-usage-card"
    >
      <div className="flex items-center gap-3">
        <Zap size={18} style={{ color: "var(--qt-cyan)" }} />
        <div>
          <div className="qt-label" style={{ color: "var(--qt-cyan)" }}>YOUR ATLAS QUOTA</div>
          <div className="text-base mt-1" style={{ color: "#fff" }}>
            {usage.remaining} of {usage.limit} {usage.kind === "monthly" ? "this month" : "free import remaining"}
          </div>
          <div className="text-xs mt-1" style={{ color: "var(--qt-text-2)" }}>
            {usage.kind === "monthly"
              ? `Resets ${nextMonthLabel(usage.month_key)} · upgrade to expand the bucket.`
              : "Free Atlas teaser. Upgrade to import unlimited portfolios on a monthly bucket."}
          </div>
        </div>
      </div>
      {usage.plan === "free" && (
        <Link to="/pricing" data-testid="atlas-usage-upgrade" className="qt-btn qt-btn-secondary">
          See upgrade plans <ArrowRight size={12} />
        </Link>
      )}
    </div>
  );
}

function PaywallCard({ usage }) {
  return (
    <div
      className="qt-card mt-6 relative"
      style={{ borderColor: "rgba(255,184,0,0.5)", background: "rgba(255,184,0,0.04)" }}
      data-testid="atlas-paywall-card"
    >
      <div className="flex items-start gap-4">
        <div
          className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl"
          style={{ border: "1px solid #FFB800", background: "rgba(255,184,0,0.12)" }}
        >
          <Lock size={18} style={{ color: "#FFB800" }} />
        </div>
        <div className="min-w-0 flex-1">
          <div className="qt-label" style={{ color: "#FFB800" }}>ATLAS QUOTA EXHAUSTED</div>
          <h3 className="text-xl font-semibold mt-1" style={{ color: "#fff" }}>
            You've used your free Atlas import.
          </h3>
          <p className="mt-2 text-sm max-w-2xl" style={{ color: "var(--qt-text-2)", lineHeight: 1.7 }}>
            Free accounts get one Atlas portfolio import to explore the product. Upgrade to a paid plan
            to keep scanning whole portfolios on a monthly bucket — perfect for treasury managers,
            funds, and security teams tracking long-tail exposure.
          </p>
          <div className="mt-4 grid grid-cols-1 sm:grid-cols-2 gap-3 max-w-2xl">
            <PlanCard
              tier="PRO"
              accent="#00E5FF"
              price="$199 / month"
              highlight="25 portfolios / month"
              features={[
                "25 Atlas imports per month",
                "100 addresses per import",
                "API access + webhooks",
                "Priority email support",
              ]}
              cta="Start Pro"
              ctaTo="/account/billing?plan=pro"
              testId="atlas-paywall-pro-cta"
            />
            <PlanCard
              tier="TEAM"
              accent="#FFB800"
              price="$999 / month"
              highlight="200 portfolios / month"
              features={[
                "200 Atlas imports per month",
                "6 seats included",
                "Slack + custom webhooks",
                "Priority + private channel",
              ]}
              cta="Start Team"
              ctaTo="/account/billing?plan=team"
              testId="atlas-paywall-team-cta"
            />
          </div>
          <div className="mt-3 text-xs qt-mono" style={{ color: "var(--qt-text-3)", letterSpacing: "0.06em" }}>
            ENTERPRISE NEEDS &gt; 200 IMPORTS/MO?{" "}
            <Link to="/audit/talk-to-sales" className="underline" style={{ color: "var(--qt-cyan)" }}>
              Talk to sales →
            </Link>
          </div>
        </div>
      </div>
    </div>
  );
}

function PlanCard({ tier, accent, price, highlight, features, cta, ctaTo, testId }) {
  return (
    <div
      className="rounded-lg p-4"
      style={{ border: `1px solid ${accent}`, background: "rgba(11,19,36,0.7)" }}
    >
      <div className="qt-label" style={{ color: accent }}>{tier}</div>
      <div className="mt-1 text-2xl font-bold qt-mono" style={{ color: "#fff", letterSpacing: "-0.02em" }}>
        {price}
      </div>
      <div className="text-sm mt-1" style={{ color: accent }}>{highlight}</div>
      <ul className="mt-3 space-y-1 text-xs" style={{ color: "var(--qt-text-2)" }}>
        {features.map((f) => (
          <li key={f} className="flex items-center gap-2">
            <Check size={11} style={{ color: accent }} /> {f}
          </li>
        ))}
      </ul>
      <Link
        to={ctaTo}
        className="qt-btn qt-btn-primary mt-4 w-full justify-center"
        data-testid={testId}
        style={{ fontSize: 13, padding: "0.6rem 1rem" }}
      >
        {cta} <ArrowRight size={12} />
      </Link>
    </div>
  );
}

function UpgradeStrip({ usage }) {
  return (
    <div
      className="qt-card mt-6 flex items-center justify-between flex-wrap gap-3"
      data-testid="atlas-upgrade-strip"
      style={{ background: "rgba(0,229,255,0.04)", borderColor: "rgba(0,229,255,0.25)" }}
    >
      <div>
        <div className="qt-label" style={{ color: "var(--qt-cyan)" }}>SCALE UP YOUR ATLAS</div>
        <div className="text-sm mt-1" style={{ color: "var(--qt-text-2)" }}>
          Upgrade to Pro for 25 portfolios per month, or Team for 200 — both include API + webhooks.
        </div>
      </div>
      <Link to="/pricing" data-testid="atlas-upgrade-strip-cta" className="qt-btn qt-btn-primary">
        Compare plans <ArrowRight size={12} />
      </Link>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Portfolio block — shared between live + demo workspaces
// ---------------------------------------------------------------------------
function PortfolioBlock({ data, demo }) {
  return (
    <>
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mt-6">
        <Metric icon={<Wallet size={18}/>}        label="SCANNED ADDRESSES"   value={data.metrics.scan_count} />
        <Metric icon={<ShieldOff size={18}/>}     label="EXPOSED ADDRESSES"   value={data.metrics.exposed_address_count} accent="#FF8000" />
        <Metric icon={<AlertTriangle size={18}/>} label="BAL AT RISK · USD"
                value={`$${(data.metrics.total_at_risk_usd || 0).toLocaleString(undefined, { maximumFractionDigits: 0 })}`}
                accent="#FF2A2A" />
        <Metric icon={<FileCode2 size={18}/>}     label="CONTRACTS ANALYZED" value={data.metrics.contract_count} />
      </div>

      {/* Severity heatmap */}
      <div className="qt-card mt-6">
        <div className="qt-label">CONTRACT FINDING HEATMAP</div>
        <div className="grid grid-cols-5 gap-2 mt-4">
          {Object.entries(data.metrics.severity_totals || {}).map(([k, v]) => (
            <div key={k} className="qt-card" style={{ padding: 14, background: "rgba(4,9,20,0.5)" }}>
              <SeverityBadge severity={k} />
              <div className="qt-mono mt-3" style={{ fontSize: 28, fontWeight: 700, letterSpacing: "-0.04em" }}>{v}</div>
            </div>
          ))}
        </div>
      </div>

      {/* Recent scans */}
      <div className="qt-card mt-6">
        <div className="qt-label">RECENT SCANS · {data.scans.length}</div>
        {data.scans.length === 0 ? (
          <Empty msg="No scans yet. Run your first scan." cta={{ to: "/scan", label: "Run a scan" }} />
        ) : (
          <table className="w-full mt-4 text-sm" data-testid="atlas-scans-table">
            <thead>
              <tr style={{ color: "var(--qt-text-3)" }}>
                <Th>CHAIN</Th><Th>ADDRESS</Th><Th>TYPE</Th><Th>BAL USD</Th><Th>EXPOSED</Th><Th>SCORE</Th><Th>TIER</Th><Th></Th>
              </tr>
            </thead>
            <tbody>
              {data.scans.map((s) => (
                <tr key={s.id} data-testid={`scan-row-${s.id}`} style={{ borderTop: "1px solid var(--qt-border)" }}>
                  <Td>{s.chain_label}</Td>
                  <Td mono>{s.address.slice(0, 10)}…{s.address.slice(-6)}</Td>
                  <Td>{s.data?.address_type}</Td>
                  <Td mono>${(s.data?.balance_usd ?? 0).toLocaleString(undefined, { maximumFractionDigits: 2 })}</Td>
                  <Td>{s.data?.pubkey_exposed ? "YES" : "no"}</Td>
                  <Td mono>{s.scoring?.score}</Td>
                  <Td><span style={{ color: tierColor(s.scoring?.tier) }}>{s.scoring?.tier}</span></Td>
                  <Td>
                    {demo ? (
                      <span className="qt-mono text-xs opacity-60">DEMO</span>
                    ) : (
                      <Link to={`/report/${s.id}`} data-testid={`open-scan-${s.id}`} className="qt-mono text-xs" style={{ color: "var(--qt-cyan)" }}>
                        REPORT →
                      </Link>
                    )}
                  </Td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      {/* Recent contracts */}
      <div className="qt-card mt-6">
        <div className="qt-label">RECENT CONTRACT ANALYSES · {data.contracts.length}</div>
        {data.contracts.length === 0 ? (
          <Empty msg="No contract analyses yet." cta={{ to: "/contract", label: "Analyze a contract" }} />
        ) : (
          <table className="w-full mt-4 text-sm" data-testid="atlas-contracts-table">
            <thead>
              <tr style={{ color: "var(--qt-text-3)" }}>
                <Th>NAME</Th><Th>VERDICT</Th><Th>CRITICAL</Th><Th>HIGH</Th><Th>MEDIUM</Th><Th>LOC</Th><Th></Th>
              </tr>
            </thead>
            <tbody>
              {data.contracts.map((c) => (
                <tr key={c.id} data-testid={`contract-row-${c.id}`} style={{ borderTop: "1px solid var(--qt-border)" }}>
                  <Td>{c.name}</Td>
                  <Td><span style={{ color: tierColor(c.verdict) }}>{c.verdict}</span></Td>
                  <Td mono>{c.summary?.Critical || 0}</Td>
                  <Td mono>{c.summary?.High || 0}</Td>
                  <Td mono>{c.summary?.Medium || 0}</Td>
                  <Td mono>{c.loc}</Td>
                  <Td>
                    {demo ? (
                      <span className="qt-mono text-xs opacity-60">DEMO</span>
                    ) : (
                      <Link to={`/report/${c.id}`} data-testid={`open-contract-${c.id}`} className="qt-mono text-xs" style={{ color: "var(--qt-cyan)" }}>
                        REPORT →
                      </Link>
                    )}
                  </Td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
    </>
  );
}

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------
function nextMonthLabel(monthKey) {
  if (!monthKey) return "next month";
  const [y, m] = monthKey.split("-").map(Number);
  const next = new Date(Date.UTC(y, m, 1)); // m is already next-month index
  return next.toLocaleDateString(undefined, { month: "long", day: "numeric" });
}

function Metric({ icon, label, value, accent }) {
  return (
    <div className="qt-card">
      <div className="flex items-center gap-2" style={{ color: "var(--qt-text-3)" }}>
        {icon}<div className="qt-label" style={{ color: "inherit" }}>{label}</div>
      </div>
      <div className="qt-mono mt-3" style={{ fontSize: 30, fontWeight: 700, letterSpacing: "-0.04em", color: accent || "#fff" }}>
        {value}
      </div>
    </div>
  );
}

function Th({ children }) { return <th className="qt-label text-left pb-2" style={{ color: "inherit" }}>{children}</th>; }
function Td({ children, mono }) {
  return <td className={`py-2 pr-3 ${mono ? "qt-mono" : ""}`} style={{ color: mono ? "#fff" : undefined }}>{children}</td>;
}

function tierColor(t) {
  return { Critical: "#FF2A2A", High: "#FF8000", Moderate: "#FFD60A", Low: "#1FDD83" }[t] || "#9CAEC9";
}

function Empty({ msg, cta }) {
  return (
    <div className="mt-4 flex items-center justify-between flex-wrap gap-3">
      <div className="text-sm" style={{ color: "var(--qt-text-2)" }}>{msg}</div>
      {cta && <Link to={cta.to} className="qt-btn qt-btn-secondary" style={{ fontSize: 12 }}>{cta.label}</Link>}
    </div>
  );
}
